// node_modules
import { action, autorun, observable } from 'mobx';
import { computedFn } from 'mobx-utils';
// utils
import { MarketFeederV2 } from 'modules/LiveData/typings';
import { FULL_SUBSCRIPTION_MODES, MARKET_FEEDER_MODES } from 'modules/LiveData/constants';

// ltpc - D0
// option_chain - D1, G
// full - D5, D1_index

export default class LiveDataStore {
  @observable tokensWithGuids: Map<string, string[]> = new Map();

  @observable LIVE_DATA: MarketFeederV2.ScripsLiveData = {};

  @observable depthTokenWithSubscriberUID: Map<string, string> = new Map();

  @observable
  openInterestValues: MarketFeederV2.OptionChainOILiveData = new Map();

  @observable
  totalVolumeValues: MarketFeederV2.OptionChainOILiveData = new Map();

  @action setOIValue = (instrumentKey: string, oi: string) => {
    this.openInterestValues.set(instrumentKey, oi);
  };

  @action setTotalVolumeValue = (
    instrumentKey: string,
    totalVolume: string,
  ) => {
    this.totalVolumeValues.set(instrumentKey, totalVolume);
  };

  ocLiveDataRun = autorun(() => {
    Object.entries(this.LIVE_DATA).forEach(
      ([instrumentKey, liveData]) => {
        const sub = liveData.currentSub;
        if (FULL_SUBSCRIPTION_MODES.includes(sub)) {
          const subObject =
            liveData[sub]?.data as MarketFeederV2.FormattedFullFeed;
          if (subObject) {
            this.setOIValue(instrumentKey, subObject.openInterest || '');
            this.setTotalVolumeValue(instrumentKey, subObject.totalVolume || '');
          }
        }
      },
    );
  });

  getByInstrumentKey = computedFn((instrumentKey: string) => {
    const scripLiveData = this.LIVE_DATA[instrumentKey];
    if (scripLiveData) {
      const sub = scripLiveData.currentSub;
      const subObject = scripLiveData[sub];

      if (sub && subObject) {
        return subObject.data;
      }
    }
  });

  getOptionParamValueWithInstrumentKey = computedFn((
    instrumentKey: string,
    param: keyof MarketFeederV2.FormattedOptionChain,
  ) => {
    const scripLiveData = this.LIVE_DATA[instrumentKey];
    if (scripLiveData) {
      const sub = scripLiveData.currentSub;
      const subObject = scripLiveData[sub];

      if (sub && subObject) {
        return (subObject.data as MarketFeederV2.FormattedOptionChain)[param];
      }
    }
  });

  getOIValueByInstrumentKeys = (instrumentKeys: string[]) =>
    instrumentKeys.map(
      instrumentKey => this.openInterestValues.get(instrumentKey) || '',
    );

  getTotalVolumeValueByInstrumentKeys = (instrumentKeys: string[]) =>
    instrumentKeys.map(
      instrumentKey => this.totalVolumeValues.get(instrumentKey) || '',
    );

  // actions
  @action setFeederData = (
    liveData: {
      formattedData: any;
      mode: MARKET_FEEDER_MODES;
      instrumentKey: string;
    }[],
  ) => {
    liveData.forEach(({ formattedData, instrumentKey, mode }) => {
      // Initialize the instrumentKey entry if it doesn't exist
      if (!this.LIVE_DATA[instrumentKey]) {
        this.LIVE_DATA[instrumentKey] = {
          currentSub: mode,
          [mode]: {
            data: formattedData,
          },
        };
        // Update or add the mode entry for the existing instrumentKey
      } else if (!this.LIVE_DATA?.[instrumentKey]?.[mode]) {
        this.LIVE_DATA[instrumentKey][mode] = {
          data: formattedData,
        };
        // Update the data for the existing instrumentKey and mode
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.LIVE_DATA[instrumentKey][mode]!.data = formattedData;
      }
      this.LIVE_DATA[instrumentKey].currentSub = mode;
    });
  };

  @action deleteLiveDataTokens = (tokens: string[]) => {
    tokens.forEach((token) => delete this.LIVE_DATA[token]);
  }

  @action setTokensWithGuids = (listenerGuid: string, tokens: string[]) => {
    this.tokensWithGuids.set(listenerGuid, tokens);
  };

  @action removeTokensWithProvidedGuid = (listenerGuid: string) =>
    this.tokensWithGuids.delete(listenerGuid);

  @action updateSubscriptionMode = (
    propertyKey: MARKET_FEEDER_MODES,
    tokens: string[],
  ) => {
    tokens.forEach(token => {
      if (this.LIVE_DATA[token]) {
        this.LIVE_DATA[token].currentSub = propertyKey;
      }
    });
  }

  @action setDepthTokenWithSubscriberUID = (
    subscriberUID: string,
    token: string,
  ) => {
    this.depthTokenWithSubscriberUID.set(subscriberUID, token);
  };

  @action removeDepthTokenWithSubscriberUID = (subscriberUID: string) =>
    this.depthTokenWithSubscriberUID.delete(subscriberUID);
}
